import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Instagram, Mail, Globe, Heart, Zap, MessageCircle, Clock, Square } from 'lucide-react';

/* ═══════════════════════════════════════════════
   PLATFORM CONFIGS
   ═══════════════════════════════════════════════ */

const PLATFORM_CONFIG = {
  instagram: {
    label: 'Instagram Automation',
    routeId: 'targets',
    icon: Instagram,
    iconSize: 24,
    gradient: 'rgba(165,145,239,.12), rgba(165,145,239,.04)',
    border: 'rgba(165,145,239,.2)',
    iconGradient: 'var(--gradient)',
  },
  gmail: {
    label: 'Gmail Automation',
    routeId: 'gmail-auto',
    icon: Mail,
    iconSize: 24,
    gradient: 'rgba(14,165,233,.12), rgba(139,92,246,.08)',
    border: 'rgba(14,165,233,.2)',
    iconGradient: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)',
  },
  tiktok: {
    label: 'TikTok Automation',
    routeId: 'tiktok-auto',
    icon: Zap,
    iconSize: 22,
    gradient: 'rgba(255,0,80,.1), rgba(0,242,234,.06)',
    border: 'rgba(255,0,80,.15)',
    iconGradient: 'linear-gradient(135deg, #ff0050, #00f2ea)',
  },
  google: {
    label: 'Google Automation',
    routeId: 'google-auto',
    icon: Globe,
    iconSize: 22,
    gradient: 'rgba(66,133,244,.1), rgba(52,168,83,.06)',
    border: 'rgba(66,133,244,.15)',
    iconGradient: 'linear-gradient(135deg, #4285F4, #34A853)',
  },
  tinder: {
    label: 'Tinder Automation',
    routeId: 'tinder-auto',
    icon: Heart,
    iconSize: 24,
    gradient: 'rgba(254,60,114,.12), rgba(254,60,114,.04)',
    border: 'rgba(254,60,114,.2)',
    iconGradient: 'linear-gradient(135deg, #FE3C72, #E0355F)',
  },
  twitter: {
    label: 'Twitter/X Automation',
    routeId: 'twitter-auto',
    icon: MessageCircle,
    iconSize: 24,
    gradient: 'rgba(29,161,242,.12), rgba(29,161,242,.04)',
    border: 'rgba(29,161,242,.2)',
    iconGradient: 'linear-gradient(135deg, #1da1f2, #0d8ecf)',
  },
};

const SERVICE_TABS = [
  { id: 'targets', label: 'Instagram', color: '#E1306C', gradient: 'linear-gradient(135deg, #833AB4, #E1306C, #F77737)',
    logo: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5"/></svg>') },
  { id: 'gmail-auto', label: 'Gmail', color: '#EA4335', gradient: 'linear-gradient(135deg, #EA4335, #FBBC05)',
    logo: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>') },
  { id: 'tiktok-auto', label: 'TikTok', color: '#00F2EA', gradient: 'linear-gradient(135deg, #000000, #00F2EA)',
    logo: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>') },
  { id: 'google-auto', label: 'Google', color: '#4285F4', gradient: 'linear-gradient(135deg, #4285F4, #34A853)',
    logo: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>') },
  { id: 'tinder-auto', label: 'Tinder', color: '#FE3C72', gradient: 'linear-gradient(135deg, #FE3C72, #FF6B6B)',
    logo: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>') },
];

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
`;

const TopBanner = styled.div`
  display: flex; align-items: center; gap: 16px;
  padding: 16px 24px; border-radius: 14px; margin-bottom: 20px;
  background: ${p => p.$runGrad
    ? `linear-gradient(135deg, ${p.$runGrad})`
    : `linear-gradient(135deg, ${p.$gradient})`};
  border: 1px solid ${p => p.$runBorder || p.$border};
  transition: all .3s;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 14px 16px;
    gap: 10px;
  }
`;

const BannerIconWrap = styled.div`
  width: 48px; height: 48px; border-radius: 12px;
  background: ${p => p.$bg || 'var(--gradient)'};
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
`;

const BannerInfo = styled.div`
  flex: 1;
  min-width: 0;
  h2 { font-size: 18px; font-weight: 800; color: var(--text-primary); margin: 0 0 2px;
    overflow: hidden; text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    h2 { font-size: 15px; }
  }
`;

const BannerMeta = styled.div`
  display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-muted);
`;

const StatusDot = styled.span`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$active ? 'var(--success)' : 'var(--error)'};
  ${p => p.$active && css`animation: ${pulse} 1.5s infinite;`}
`;

const SwitcherWrap = styled.div`
  display: flex; align-items: center; gap: 6px; margin-left: auto;
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
    &::-webkit-scrollbar { height: 3px; }
  }
`;

const TabBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 10px; border: 2px solid transparent;
  font-size: 11px; font-weight: 700; cursor: pointer;
  transition: all .2s ease;
  background: ${p => p.$active ? p.$gradient || p.$color : 'var(--surface)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  border-color: ${p => p.$active ? 'transparent' : 'var(--border)'};
  opacity: ${p => p.$active ? 1 : .7};
  &:hover { opacity: 1; transform: translateY(-1px);
    ${p => !p.$active && `border-color: ${p.$color}40; background: ${p.$color}10;`}
  }
  @media (max-width: 768px) {
    flex-shrink: 0;
    padding: 6px 10px;
    span { display: none; }
  }
`;

const TabLogo = styled.img`
  width: 16px; height: 16px; border-radius: 3px;
`;

const TimerBadge = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 10px;
  font-size: 13px; font-weight: 700; font-family: monospace;
  background: ${p => p.$bg || 'rgba(34,197,94,.12)'};
  color: ${p => p.$color || 'var(--success)'};
`;

const StopBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 10px; border: none;
  font-size: 12px; font-weight: 700; cursor: pointer;
  background: rgba(239,68,68,.12); color: #ef4444;
  transition: all .2s;
  &:hover { background: rgba(239,68,68,.2); transform: translateY(-1px); }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

/**
 * Shared automation page banner with service switcher tabs.
 *
 * @param {string}   platform     - 'instagram' | 'gmail' | 'tiktok' | 'google' | 'tinder'
 * @param {string}   deviceName   - Device display name or ID
 * @param {boolean}  isConnected  - Whether the device is connected
 * @param {boolean}  isRunning    - Whether a script is running (optional)
 * @param {string}   timer        - Running timer display string (optional)
 * @param {object}   runColor     - { bg: 'rgba(...,', main: '#xxx' } for running state color (optional)
 * @param {function} onNavigate   - setActiveTab callback for switching pages
 * @param {function} onStop       - Stop script callback (optional)
 * @param {React.ReactNode} children - Extra content to render inside the banner (optional)
 */
const AutomationBanner = ({ platform, deviceName, isConnected, isRunning, timer, runColor, onNavigate, onStop, children }) => {
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.instagram;
  const Icon = config.icon;

  // Running state gradient override
  let runGrad = null;
  let runBorder = null;
  if (isRunning && runColor) {
    runGrad = `${runColor.bg}0.12), ${runColor.bg}0.04)`;
    runBorder = `${runColor.bg}0.25)`;
  }

  return (
    <TopBanner $gradient={config.gradient} $border={config.border} $runGrad={runGrad} $runBorder={runBorder}>
      <BannerIconWrap $bg={config.iconGradient}>
        <Icon size={config.iconSize} />
      </BannerIconWrap>
      <BannerInfo>
        <h2>{config.label}</h2>
        <BannerMeta>
          {deviceName}
          <StatusDot $active={isConnected} />
          {isConnected ? 'Connecté' : 'Inactif'}
        </BannerMeta>
      </BannerInfo>

      <SwitcherWrap>
        {SERVICE_TABS.map(svc => (
          <TabBtn
            key={svc.id}
            $active={svc.id === config.routeId}
            $color={svc.color}
            $gradient={svc.gradient}
            onClick={() => svc.id !== config.routeId && onNavigate?.(svc.id)}
            title={`${svc.label} Automation`}
          >
            <TabLogo src={svc.logo} alt="" />
            <span>{svc.label}</span>
          </TabBtn>
        ))}
      </SwitcherWrap>

      {isRunning && timer && (
        <TimerBadge $bg={runColor ? `${runColor.bg}0.15)` : undefined} $color={runColor?.main}>
          <Clock size={16} /> {timer}
        </TimerBadge>
      )}
      {isRunning && onStop && (
        <StopBtn onClick={onStop}>
          <Square size={13} /> Arrêter
        </StopBtn>
      )}
      {children}
    </TopBanner>
  );
};

export default AutomationBanner;
